import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  fetchMe,
  getAuthToken,
  loginUser,
  registerUser,
  setAuthToken,
  setUnauthorizedHandler,
  updateAccountLogin,
  updateAccountPassword,
  uploadAccountAvatar,
} from '../api/client';
import type { AuthUser } from '../types';

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<void>;
  register: (login: string, password: string, name?: string) => Promise<void>;
  changeLogin: (login: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(() => getAuthToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setAuthToken(null);
    setTokenState(null);
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let active = true;
    setAuthToken(token);
    void fetchMe()
      .then((data) => {
        if (active) setUser(data.user);
      })
      .catch(() => {
        if (active) logout();
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token, logout]);

  const login = useCallback(async (loginValue: string, password: string) => {
    const response = await loginUser(loginValue, password);
    setAuthToken(response.token);
    setTokenState(response.token);
    setUser(response.user);
  }, []);

  const register = useCallback(async (loginValue: string, password: string, name?: string) => {
    const response = await registerUser(loginValue, password, name);
    setAuthToken(response.token);
    setTokenState(response.token);
    setUser(response.user);
  }, []);

  const changeLogin = useCallback(async (loginValue: string) => {
    const response = await updateAccountLogin(loginValue);
    setAuthToken(response.token);
    setTokenState(response.token);
    setUser(response.user);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await updateAccountPassword(currentPassword, newPassword);
  }, []);

  const uploadAvatar = useCallback(async (file: File) => {
    const response = await uploadAccountAvatar(file);
    setUser(response.user);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    token,
    loading,
    login,
    register,
    changeLogin,
    changePassword,
    uploadAvatar,
    logout,
  }), [user, token, loading, login, register, changeLogin, changePassword, uploadAvatar, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
